import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stack,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  LocationOn,
  Map,
  MyLocation,
  Search,
  Close,
  GpsFixed,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import odcService from '../services/odcService';
import spopService from '../services/spopService';
import { ODC, SPOP } from '../types';

const ODCForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [mapZoom, setMapZoom] = useState(15);
  const [mapCenter, setMapCenter] = useState({lat: -6.2088, lng: 106.8456});
  const [isDraggingMap, setIsDraggingMap] = useState(false);
  const [spops, setSPOPs] = useState<SPOP[]>([]);

  const [formData, setFormData] = useState({
    spopId: '',
    odcCode: '',
    address: '',
    radius: '',
    latitude: '',
    longitude: '',
    description: '',
    isActive: true,
  });

  useEffect(() => {
    fetchSPOPs();
    if (isEdit && id) {
      fetchODC();
    }
  }, [isEdit, id]);

  const fetchSPOPs = async () => {
    try {
      const response = await spopService.getSPOPs(1, 100);
      if (response.data?.success) {
        setSPOPs(response.data.data.spops);
      }
    } catch (err) {
      console.error('Error fetching SPOPs:', err);
    }
  };

  const fetchODC = async () => {
    try {
      setLoading(true);
      const response = await odcService.getODCById(id!);
      if (response.success) {
        const odc = response.data;
        setFormData({
          spopId: odc.spopId,
          odcCode: odc.odcCode.split('-').slice(1).join('-'), // Remove SPOP code part
          address: odc.address,
          radius: odc.radius.toString(),
          latitude: odc.latitude.toString(),
          longitude: odc.longitude.toString(),
          description: odc.description || '',
          isActive: odc.isActive,
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Gagal memuat data ODC');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSelectChange = (field: string) => (event: any) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation tidak didukung oleh browser ini');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString(),
        });
        setGettingLocation(false);
      },
      (error) => {
        setError('Gagal mendapatkan lokasi: ' + error.message);
        setGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const openMapDialog = () => {
    setMapDialogOpen(true);
    setSearchAddress('');
    setSearchResults([]);
    setSelectedLocation(null);
  };

  const closeMapDialog = () => {
    setMapDialogOpen(false);
    setSearchAddress('');
    setSearchResults([]);
    setSelectedLocation(null);
  };

  const searchLocation = async () => {
    if (!searchAddress.trim()) return;
    
    setSearchLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}&limit=5&countrycodes=id`
      );
      const data = await response.json();
      
      const results = data.map((item: any) => ({
        display_name: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
      }));
      
      setSearchResults(results);
    } catch (err) {
      setError('Gagal mencari lokasi. Silakan coba lagi.');
    } finally {
      setSearchLoading(false);
    }
  };

  const selectLocation = (location: {lat: number, lon: number}) => {
    setSelectedLocation({lat: location.lat, lng: location.lon});
    setMapCenter({lat: location.lat, lng: location.lon});
    setMapZoom(18);
  };

  const confirmLocation = () => {
    if (selectedLocation) {
      setFormData({
        ...formData,
        latitude: selectedLocation.lat.toString(),
        longitude: selectedLocation.lng.toString(),
      });
      closeMapDialog();
    }
  };

  const openInMaps = () => {
    if (formData.latitude && formData.longitude) {
      const url = `https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`;
      window.open(url, '_blank');
    }
  };

  const generateODCCode = (spopId: string, odcCode: string) => {
    const selectedSPOP = spops.find(spop => spop.id === spopId);
    if (selectedSPOP && odcCode) {
      return `${selectedSPOP.areaCode}-${odcCode}`;
    }
    return odcCode;
  };

  const validateForm = () => {
    const { spopId, odcCode, address, radius, latitude, longitude } = formData;
    
    if (!spopId) return 'SPOP wajib dipilih';
    if (!odcCode.trim()) return 'ODC Code wajib diisi';
    if (!address.trim()) return 'Alamat wajib diisi';
    if (!radius.trim()) return 'Radius wajib diisi';
    if (!latitude.trim()) return 'Latitude wajib diisi';
    if (!longitude.trim()) return 'Longitude wajib diisi';

    const radiusNum = parseInt(radius);
    if (isNaN(radiusNum) || radiusNum < 1 || radiusNum > 100000) {
      return 'Radius harus berupa angka antara 1 hingga 100000 meter';
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      return 'Latitude harus berupa angka antara -90 hingga 90';
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      return 'Longitude harus berupa angka antara -180 hingga 180';
    }

    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        spopId: formData.spopId,
        odcCode: formData.odcCode.trim(), // Send only the ODC code part
        address: formData.address.trim(),
        radius: parseInt(formData.radius),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        description: formData.description.trim(),
        isActive: formData.isActive,
      };

      if (isEdit) {
        await odcService.updateODC(id!, submitData);
        setSuccess('ODC berhasil diperbarui');
      } else {
        await odcService.createODC(submitData);
        setSuccess('ODC berhasil dibuat');
      }

      setTimeout(() => {
        navigate('/master-data/odc');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || `Gagal ${isEdit ? 'memperbarui' : 'membuat'} ODC`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/master-data/odc')}
        >
          Kembali
        </Button>
        <Typography variant="h4" fontWeight="bold">
          {isEdit ? 'Edit ODC' : 'Tambah ODC Baru'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Informasi Dasar
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth required>
                  <InputLabel>Pilih SPOP</InputLabel>
                  <Select
                    value={formData.spopId}
                    onChange={handleSelectChange('spopId')}
                    label="Pilih SPOP"
                  >
                    {spops.map((spop) => (
                      <MenuItem key={spop.id} value={spop.id}>
                        {spop.name} ({spop.areaCode})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="ODC Code"
                  value={formData.odcCode}
                  onChange={handleInputChange('odcCode')}
                  required
                  placeholder="Contoh: ODC-001"
                  helperText={formData.spopId ? `Full Code: ${generateODCCode(formData.spopId, formData.odcCode)}` : 'Pilih SPOP terlebih dahulu'}
                />
              </Stack>

              <TextField
                fullWidth
                label="Alamat"
                value={formData.address}
                onChange={handleInputChange('address')}
                required
                multiline
                rows={3}
                placeholder="Alamat lengkap ODC"
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  label="Radius (meter)"
                  type="number"
                  value={formData.radius}
                  onChange={handleInputChange('radius')}
                  required
                  inputProps={{ min: 1, max: 100000 }}
                  helperText="Jangkauan layanan dalam meter (1-100000)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleSwitchChange('isActive')}
                    />
                  }
                  label="Status Aktif"
                />
              </Stack>

              {/* Location Information */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Informasi Lokasi
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Box>

              <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
                  <LocationOn color="primary" />
                  <Typography variant="subtitle2">
                    Koordinat Lokasi ODC
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={gettingLocation ? <CircularProgress size={16} /> : <GpsFixed />}
                    onClick={getCurrentLocation}
                    disabled={gettingLocation}
                  >
                    {gettingLocation ? 'Mendapatkan Lokasi...' : 'GPS Saat Ini'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Search />}
                    onClick={openMapDialog}
                    color="secondary"
                  >
                    Cari Lokasi
                  </Button>
                  {formData.latitude && formData.longitude && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Map />}
                      onClick={openInMaps}
                    >
                      Lihat di Maps
                    </Button>
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    type="number"
                    value={formData.latitude}
                    onChange={handleInputChange('latitude')}
                    required
                    inputProps={{ step: 'any', min: -90, max: 90 }}
                    placeholder="-6.200000"
                    helperText="Koordinat lintang (-90 hingga 90)"
                  />
                  <TextField
                    fullWidth
                    label="Longitude"
                    type="number"
                    value={formData.longitude}
                    onChange={handleInputChange('longitude')}
                    required
                    inputProps={{ step: 'any', min: -180, max: 180 }}
                    placeholder="106.816666"
                    helperText="Koordinat bujur (-180 hingga 180)"
                  />
                </Stack>
              </Paper>

              <TextField
                fullWidth
                label="Deskripsi (Opsional)"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
                placeholder="Deskripsi tambahan tentang ODC ini..."
              />

              {/* Submit Button */}
              <Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/master-data/odc')}
                >
                  Batal
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  disabled={loading}
                >
                  {loading ? 'Menyimpan...' : (isEdit ? 'Perbarui' : 'Simpan')}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Map Search Dialog - Enhanced with Interactive Map */}
      <Dialog 
        open={mapDialogOpen} 
        onClose={closeMapDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Cari Lokasi ODC</Typography>
            <IconButton onClick={closeMapDialog} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            {/* Search Input */}
            <TextField
              fullWidth
              label="Cari Alamat"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              placeholder="Contoh: Jl. Sudirman Jakarta, Mall Taman Anggrek, dll"
              onKeyPress={(e) => e.key === 'Enter' && searchLocation()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={searchLocation}
                      disabled={!searchAddress.trim() || searchLoading}
                    >
                      {searchLoading ? <CircularProgress size={20} /> : <Search />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hasil Pencarian:
                </Typography>
                <Stack spacing={1} maxHeight={200} sx={{ overflowY: 'auto' }}>
                  {searchResults.map((result, index) => (
                    <Paper
                      key={index}
                      sx={{
                        p: 2,
                        cursor: 'pointer',
                        border: selectedLocation?.lat === result.lat && selectedLocation?.lng === result.lon 
                          ? '2px solid' 
                          : '1px solid',
                        borderColor: selectedLocation?.lat === result.lat && selectedLocation?.lng === result.lon 
                          ? 'primary.main' 
                          : 'grey.300',
                        '&:hover': {
                          bgcolor: 'grey.50',
                        },
                      }}
                      onClick={() => selectLocation(result)}
                    >
                      <Typography variant="body2" fontWeight="medium">
                        {result.display_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        {result.lat.toFixed(6)}, {result.lon.toFixed(6)}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Interactive Map Container */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Peta Interaktif - Klik untuk menandai lokasi:
              </Typography>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 400,
                  border: '2px solid',
                  borderColor: 'grey.300',
                  borderRadius: 2,
                  overflow: 'hidden',
                  backgroundColor: 'grey.100',
                }}
              >
                {/* OpenStreetMap iframe */}
                <iframe
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                    mapCenter.lng - (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lat - (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lng + (0.001 * Math.pow(2, 18 - mapZoom))
                  },${
                    mapCenter.lat + (0.001 * Math.pow(2, 18 - mapZoom))
                  }&layer=mapnik`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                  title="Interactive Map"
                />
                
                {/* Custom Draggable Marker */}
                {selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${50 + ((selectedLocation.lng - mapCenter.lng) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                      top: `${50 - ((selectedLocation.lat - mapCenter.lat) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                      transform: 'translate(-50%, -100%)',
                      cursor: 'move',
                      zIndex: 1001,
                      transition: 'all 0.1s ease',
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startLat = selectedLocation.lat;
                      const startLng = selectedLocation.lng;
                      const rect = e.currentTarget.parentElement!.getBoundingClientRect();
                      
                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Convert pixel movement to coordinate change
                        const mapWidth = rect.width;
                        const mapHeight = rect.height;
                        const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                        
                        const latChange = -(deltaY / mapHeight) * zoomFactor * 2;
                        const lngChange = (deltaX / mapWidth) * zoomFactor * 2;
                        
                        setSelectedLocation({
                          lat: startLat + latChange,
                          lng: startLng + lngChange,
                        });
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                  >
                    {/* Marker Pin */}
                    <Box
                      sx={{
                        width: 30,
                        height: 40,
                        background: 'linear-gradient(45deg, #ff4444, #cc0000)',
                        borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                        border: '3px solid white',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 6px 12px rgba(0,0,0,0.4)',
                        },
                      }}
                    >
                      📍
                    </Box>
                    {/* Drag Tooltip */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -45,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '10px',
                        whiteSpace: 'nowrap',
                        opacity: 0.9,
                        pointerEvents: 'none',
                      }}
                    >
                      Drag untuk memindahkan
                    </Box>
                  </Box>
                )}
                
                {/* Enhanced Zoom Controls */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 1000,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ 
                      minWidth: 35, 
                      height: 35, 
                      fontSize: '18px',
                      backgroundColor: 'white',
                      color: 'black',
                      '&:hover': { backgroundColor: 'grey.100' },
                      boxShadow: 2,
                    }}
                    onClick={() => {
                      setMapZoom(prev => Math.min(prev + 1, 19));
                    }}
                  >
                    +
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ 
                      minWidth: 35, 
                      height: 35, 
                      fontSize: '18px',
                      backgroundColor: 'white',
                      color: 'black',
                      '&:hover': { backgroundColor: 'grey.100' },
                      boxShadow: 2,
                    }}
                    onClick={() => {
                      setMapZoom(prev => Math.max(prev - 1, 10));
                    }}
                  >
                    −
                  </Button>
                </Box>
                
                {/* Map Interaction Overlay - for panning and marker placement */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: isDraggingMap ? 'grabbing' : (selectedLocation ? 'move' : 'crosshair'),
                    backgroundColor: 'transparent',
                    zIndex: 998,
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    
                    // Check if Ctrl or Shift is pressed for map panning
                    if (e.ctrlKey || e.shiftKey) {
                      setIsDraggingMap(true);
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startMapCenter = { ...mapCenter };
                      const rect = e.currentTarget.getBoundingClientRect();
                      
                      const handleMapDrag = (moveEvent: MouseEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Convert pixel movement to coordinate change for map panning
                        const mapWidth = rect.width;
                        const mapHeight = rect.height;
                        const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                        
                        const latChange = (deltaY / mapHeight) * zoomFactor * 2;
                        const lngChange = -(deltaX / mapWidth) * zoomFactor * 2;
                        
                        setMapCenter({
                          lat: startMapCenter.lat + latChange,
                          lng: startMapCenter.lng + lngChange,
                        });
                      };
                      
                      const handleMapDragEnd = () => {
                        setIsDraggingMap(false);
                        document.removeEventListener('mousemove', handleMapDrag);
                        document.removeEventListener('mouseup', handleMapDragEnd);
                      };
                      
                      document.addEventListener('mousemove', handleMapDrag);
                      document.addEventListener('mouseup', handleMapDragEnd);
                    }
                  }}
                  onClick={(e) => {
                    // Only place marker if not dragging map and no modifier keys pressed
                    if (!isDraggingMap && !e.ctrlKey && !e.shiftKey) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const x = e.clientX - rect.left;
                      const y = e.clientY - rect.top;
                      
                      // Enhanced coordinate calculation
                      const mapWidth = rect.width;
                      const mapHeight = rect.height;
                      
                      const zoomFactor = 0.001 * Math.pow(2, 18 - mapZoom);
                      
                      const lat = mapCenter.lat - ((y / mapHeight - 0.5) * zoomFactor * 2);
                      const lng = mapCenter.lng + ((x / mapWidth - 0.5) * zoomFactor * 2);
                      
                      setSelectedLocation({ lat, lng });
                    }
                  }}
                />
                
                {/* Crosshair for precise clicking (only when no marker) */}
                {!selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      zIndex: 999,
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        border: '2px solid red',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 4,
                          backgroundColor: 'red',
                          borderRadius: '50%',
                        }}
                      />
                    </Box>
                  </Box>
                )}
                
                {/* Grid Lines for better precision (optional) */}
                {selectedLocation && (
                  <>
                    {/* Vertical line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${50 + ((selectedLocation.lng - mapCenter.lng) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        pointerEvents: 'none',
                        zIndex: 997,
                      }}
                    />
                    {/* Horizontal line */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: `${50 - ((selectedLocation.lat - mapCenter.lat) / (0.001 * Math.pow(2, 18 - mapZoom) * 2)) * 50}%`,
                        left: 0,
                        right: 0,
                        height: '1px',
                        backgroundColor: 'rgba(255, 0, 0, 0.3)',
                        pointerEvents: 'none',
                        zIndex: 997,
                      }}
                    />
                  </>
                )}
                
                {/* Map Pan Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    backgroundColor: 'rgba(33, 150, 243, 0.9)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '11px',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  🖱️ {isDraggingMap ? 'Dragging Map...' : 'Ctrl/Shift + Drag = Pan Map'}
                </Box>
                
                {/* Current Location Indicator */}
                {selectedLocation && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 10,
                      left: 10,
                      backgroundColor: 'rgba(76, 175, 80, 0.9)',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '11px',
                      zIndex: 1000,
                    }}
                  >
                    📍 {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </Box>
                )}
                
                {/* Zoom Level Indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '12px',
                    zIndex: 1000,
                  }}
                >
                  🔍 Zoom: {mapZoom}
                </Box>
              </Box>
            </Box>

            {/* Selected Location Preview */}
            {selectedLocation && (
              <Paper sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                  Lokasi Terpilih:
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  Latitude: {selectedLocation.lat.toFixed(6)}
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  Longitude: {selectedLocation.lng.toFixed(6)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Map />}
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`;
                    window.open(url, '_blank');
                  }}
                  sx={{ mt: 1 }}
                >
                  Preview di Google Maps
                </Button>
              </Paper>
            )}

            {/* Instructions */}
            <Alert severity="info">
              <Typography variant="body2">
                💡 <strong>Cara menggunakan peta interaktif:</strong>
                <br />
                <strong>Metode 1:</strong> Ketik alamat → Pilih dari hasil → Klik "Gunakan Lokasi Ini"
                <br />
                <strong>Metode 2:</strong> Klik langsung di peta untuk menandai lokasi
                <br />
                <strong>Metode 3:</strong> Drag marker yang sudah ada untuk menyesuaikan posisi
                <br />
                <strong>Pan Map:</strong> Tahan Ctrl/Shift + Drag untuk menggeser peta
                <br />
                <strong>Zoom:</strong> Gunakan tombol +/- di kanan atas peta
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapDialog}>
            Batal
          </Button>
          <Button 
            variant="contained" 
            onClick={confirmLocation}
            disabled={!selectedLocation}
            startIcon={<LocationOn />}
          >
            Gunakan Lokasi Ini
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ODCForm;
